/**
 * Central mapping from physical input to abstract commands (spec: Accessibility / interaction:
 * "centralise focus-order metadata and input commands"). Components react to commands, not keys,
 * so a later Smart TV remote or gamepad adapter only has to extend this table.
 */

export type InputCommand = 'back' | 'activate' | 'previous' | 'next' | 'first' | 'last';

const KEY_COMMANDS: Readonly<Record<string, InputCommand>> = {
  Escape: 'back',
  // Smart TV remotes commonly report these for their Back button.
  GoBack: 'back',
  BrowserBack: 'back',
  Enter: 'activate',
  ' ': 'activate',
  ArrowLeft: 'previous',
  ArrowUp: 'previous',
  ArrowRight: 'next',
  ArrowDown: 'next',
  Home: 'first',
  End: 'last',
};

/** The command for a key event, ignoring presses combined with modifier keys. */
export function commandForKey(event: {
  key: string;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
}): InputCommand | null {
  if (event.altKey || event.ctrlKey || event.metaKey) return null;
  return KEY_COMMANDS[event.key] ?? null;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Keyboard-focusable descendants in DOM (= focus) order. */
export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true',
  );
}
